import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Text from '@zenbusiness/common-components/Text';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { Card } from '#client/components/Card';

import { MT_DATA_TRACKING, WEBSITE_PRODUCTS } from '../../utils/constants';

const dataMap = new Map([
  [WEBSITE_PRODUCTS.WEBSITE, {
    url: 'websites',
    icon: '/s2/icon/webpage-outline.svg',
    name: 'website'
  }],
  [WEBSITE_PRODUCTS.DOMAIN, {
    url: 'domains',
    icon: '/s2/icon/globe-outline.svg',
    name: 'domain'
  }],
  [WEBSITE_PRODUCTS.EMAIL, {
    url: 'https://mail.zenbusiness.com/',
    icon: '/s2/icon/send-message-outline.svg',
    name: 'email'
  }]
]);

const TooltipContent = (
  <>
    <Typography variant="body_smallBold">Why is this locked?</Typography>
    <br />
    <Typography component="p" variant="caption_largeRegular" fontWeight={400}>
      Your email configuration is being set up.
      You&apos;ll get an email when this is ready to use.
    </Typography>
  </>
);

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    color: theme.palette.focus00.main,
    backgroundColor: theme.palette.focus90.main,
    borderRadius: '12px',
    padding: theme.spacing(2, 3)
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  '&.MuiCard-root': {
    border: `1px solid ${theme.palette.focus30.light}`
  },

  '&.locked': {
    cursor: 'unset'
  }
}));

const StyledCardBody = styled(Card.Body)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),

  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(2)
  }
}));

const StyledCardImage = styled(Card.Image)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.focus90.main,
  alignItems: 'center',

  '& > img': {
    width: 24,
    height: 24,
    filter: 'invert(1)'
  },

  [theme.breakpoints.up('lg')]: {
    width: 56,
    height: 56,

    '& > img': {
      width: 32,
      height: 32
    }
  }
}));

const StyledCardTitle = styled(Card.Title)(({
  fontSize: '1.125rem',
  textTransform: 'capitalize',
  lineHeight: '1rem'
}));

const QuickActionsCard = ({ CardProps, type, showLockIcon }) => {
  const { icon, url, name } = dataMap.get(type);

  const trackClick = useButtonPressedCallback({
    ...MT_DATA_TRACKING,
    elementName: 'quick_action_card'
  });

  return (
    <StyledCard
      {...CardProps}
      variant="outlined"
      shadow="none"
      className={classNames({ locked: showLockIcon })}
      tag={showLockIcon ? ButtonBase : Link}
      to={url}
      onClick={trackClick}
    >
      <StyledCardBody>
        <StyledCardImage src={icon} />
        <StyledCardTitle>
          <Text size="md">
            {name}
          </Text>
        </StyledCardTitle>
        { showLockIcon && (
          <StyledTooltip
            title={TooltipContent}
            followCursor
          >
            <Box
              sx={(theme) => ({
                position: 'absolute',
                inset: 0,
                '&:hover': {
                  '& div': {
                    backgroundColor: theme.palette.focus90.main,
                    color: theme.palette.focus00.main
                  }
                }
              })}
            >
              <Box sx={(theme) => ({
                position: 'absolute',
                right: {
                  xs: 5,
                  md: 20
                },
                top: {
                  md: 10
                },
                padding: theme.spacing(0.25, 1),
                borderRadius: 50
              })}
              >
                <LockIcon />
              </Box>
            </Box>
          </StyledTooltip>
        )}
      </StyledCardBody>
    </StyledCard>
  );
};

QuickActionsCard.propTypes = {
  CardProps: PropTypes.shape({}),
  type: PropTypes.oneOf(Object.values(WEBSITE_PRODUCTS)).isRequired,
  showLockIcon: PropTypes.bool
};

QuickActionsCard.defaultProps = {
  showLockIcon: false
};

export default QuickActionsCard;
